import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import SearchFilterTable from '../../components/SearchFilterTable';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { 
  Building,
  Users,
  Calendar,
  Loader2,
  Shield,
  ShieldOff,
  ChevronRight,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { getUserErrorMessage } from '../../utils/errorMessages';

const AdminOrganizations = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const [deleteModal, setDeleteModal] = useState({ open: false, org: null });
  const [deleting, setDeleting] = useState(false);

  const fetchOrganizations = async () => {
    try {
      const response = await api.get('/admin/organizations');
      setOrganizations(response.data);
    } catch (error) {
      toast.error('Organisaties laden mislukt');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, [api]);

  const handleDeleteOrg = async () => {
    if (!deleteModal.org) return;
    
    setDeleting(true);
    try {
      await api.delete(`/admin/organizations/${deleteModal.org.id}`);
      toast.success(`Organisatie "${deleteModal.org.name}" verwijderd`);
      setDeleteModal({ open: false, org: null });
      fetchOrganizations();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Verwijderen mislukt'));
    } finally {
      setDeleting(false);
    }
  };

  const getCWIColor = (score) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 50) return 'text-orange-500';
    return 'text-red-500';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Onbekend';
    try {
      return new Date(dateString).toLocaleDateString('nl-NL', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Onbekend';
    }
  };

  // Get unique frameworks for filter
  const frameworkOptions = [...new Set(organizations.map(o => o.framework).filter(Boolean))]
    .map(fw => ({ value: fw, label: fw }));

  const columns = [
    { key: 'name', label: 'Organisatie' },
    { key: 'admin_access', label: 'Admin Toegang', className: 'text-center' },
    { key: 'cwi_score', label: 'CWI Score', className: 'text-center' },
    { key: 'total_risks', label: "Risico's", className: 'text-center' },
    { key: 'user_count', label: 'Gebruikers', className: 'text-center' },
    { key: 'framework', label: 'Framework' },
    { key: 'actions', label: '', className: 'text-right' }
  ];

  const filters = frameworkOptions.length > 0 ? [
    { key: 'framework', label: 'Framework', options: frameworkOptions }
  ] : [];

  const renderRow = (org, index, total) => (
    <tr 
      key={org.id} 
      className={`border-b border-neutral-100 dark:border-neutral-800 last:border-0 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors cursor-pointer ${
        index % 2 === 1 ? 'bg-neutral-50/50 dark:bg-neutral-800/20' : ''
      }`}
      data-testid={`org-row-${org.id}`}
      onClick={() => navigate(`/admin/organizations/${org.id}`)}
    >
      <td className="p-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-neutral-100 flex items-center justify-center">
            <Building className="h-5 w-5 text-neutral-600" strokeWidth={1.5} />
          </div>
          <p className="text-sm font-medium text-black">{org.name}</p>
        </div>
      </td>
      <td className="p-4 text-center">
        {org.allow_admin_access ? (
          <Badge className="bg-emerald-100 text-emerald-700">
            <Shield className="h-3 w-3 mr-1" />
            Ja
          </Badge>
        ) : (
          <Badge className="bg-neutral-100 text-neutral-500">
            <ShieldOff className="h-3 w-3 mr-1" />
            Nee
          </Badge>
        )}
      </td>
      <td className="p-4 text-center">
        <span className={`text-2xl font-semibold ${getCWIColor(org.cwi_score)}`}>
          {org.cwi_score}
        </span>
      </td>
      <td className="p-4 text-center">
        <span className="text-sm text-neutral-600">{org.total_risks}</span>
      </td>
      <td className="p-4 text-center">
        <div className="flex items-center justify-center gap-1">
          <Users className="h-4 w-4 text-neutral-400" strokeWidth={1.5} />
          <span className="text-sm text-neutral-600">{org.user_count}</span>
        </div>
      </td>
      <td className="p-4">
        {org.framework ? (
          <Badge className="border-transparent bg-neutral-100 text-neutral-700">
            {org.framework}
          </Badge>
        ) : (
          <span className="text-sm text-neutral-400">-</span>
        )}
      </td>
      <td className="p-4 text-right">
        <div className="flex items-center justify-end gap-2">
          <Button
            size="sm"
            variant="outline"
            className="border-red-200 text-red-600 hover:bg-red-50"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteModal({ open: true, org });
            }}
            data-testid={`delete-org-${org.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
          <ChevronRight className="h-5 w-5 text-neutral-400" strokeWidth={1.5} />
        </div>
      </td>
    </tr>
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-neutral-400" strokeWidth={1.5} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-1">
              Admin
            </p>
            <h1 className="text-2xl font-bold tracking-tight text-black">
              Organisaties
            </h1>
          </div>
        </div>

        {/* Organizations Table */}
        <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
          <CardContent className="p-4">
            <SearchFilterTable
              data={organizations}
              columns={columns}
              searchKeys={['name', 'framework']}
              filters={filters}
              renderRow={renderRow}
              searchPlaceholder="Zoek organisatie..."
              emptyMessage="Geen organisaties gevonden"
            />
          </CardContent>
        </Card>
      </div>
      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, org: deleteModal.org })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Organisatie Verwijderen
            </DialogTitle>
            <DialogDescription>
              Weet je zeker dat je <strong>"{deleteModal.org?.name}"</strong> wilt verwijderen?
              Dit verwijdert ook alle bijbehorende data (gebruikers, assets, risico's, maatregelen, etc.).
            </DialogDescription>
          </DialogHeader>
          
          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
            <p className="text-sm text-red-800">
              <strong>Let op:</strong> Deze actie kan niet ongedaan worden gemaakt.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModal({ open: false, org: null })}>
              Annuleren
            </Button>
            <Button 
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={handleDeleteOrg}
              disabled={deleting}
              data-testid="confirm-delete-org"
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Verwijderen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminOrganizations;
